/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-09 08:55:14
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { DASHBOARD_BLOCKS_DEFAULT } from 'src/constants/dashboard-layout.js';

import apiService from 'src/services/api-service';
import {
  GET_DASHBOARD_BLOCKS_REQUEST,
  GET_DASHBOARD_BLOCKS_SUCCESS,
  GET_DASHBOARD_BLOCKS_FAILURE,
  GET_DASHBOARD_COMPONENTS_REQUEST,
  GET_DASHBOARD_COMPONENTS_SUCCESS,
  GET_DASHBOARD_COMPONENTS_FAILURE,
  FEEDBACK_REQUEST,
  FEEDBACK_SUCCESS,
  FEEDBACK_FAILURE,
  PROCESS_DISMISS_BLOCK_REQUEST,
  PROCESS_DISMISS_BLOCK_SUCCESS,
  PROCESS_DISMISS_BLOCK_FAILURE,
  UNDO_DISMISS_BLOCK_REQUEST,
  UNDO_DISMISS_BLOCK_SUCCESS,
  UNDO_DISMISS_BLOCK_FAILURE,
  CONFIRM_DISMISS_BLOCK,
  UPDATE_DASHBOARD_BLOCK
} from 'stores/flow-type/dashboard.type';

/**
 * call to admin dashboard block api
 * @return {object} The RSAA object for API middleware
 */
export const getDashboardBlocks = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-dashboard-blocks', {}, [
    GET_DASHBOARD_BLOCKS_REQUEST,
    {
      type: GET_DASHBOARD_BLOCKS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_DASHBOARD_BLOCKS_FAILURE,
      payload: async (action, state, res) => {
        return { data: DASHBOARD_BLOCKS_DEFAULT };
      }
    }
  ]);
};
/**
 * call to admin dashboard component api
 * @return {object} The RSAA object for API middleware
 */
export const getDashboardComponents = (language: string, i18n: I18n) => {
  const queries = { language };
  return apiService.sendRequest('admin-v3.dashboard-blocks.get-dashboard-components', { queries }, [
    GET_DASHBOARD_COMPONENTS_REQUEST,
    {
      type: GET_DASHBOARD_COMPONENTS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_DASHBOARD_COMPONENTS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Dashboard.Actions.getDashboardComponents'),
          i18n
        )
    }
  ]);
};

/**
 * send feedback from dashboad
 * @return {object} The RSAA object for API middleware
 */
export const sendFeedback = (body: Object, blockName: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.feedback-dashboard-block',
    { params: { blockId: blockName }, body },
    [
      { type: FEEDBACK_REQUEST, payload: { blockName } },
      {
        type: FEEDBACK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data, blockName };
        }
      },
      {
        type: FEEDBACK_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Dashboard.Actions.sendFeedback'),
            i18n
          );
          return { ...data, blockName };
        }
      }
    ]
  );
};

/**
 * update dashboard blocks list after dismiss
 */
export const confirmDismissBlock = (blockName: string) => {
  return { type: CONFIRM_DISMISS_BLOCK, payload: { blockName } };
};

/**
 * call to dismiss a block in dashboard blocks list
 * @return {object} The RSAA object for API middleware
 */

export const processDismissBlock = (blockName: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.change-dashboard-block',
    { body: { [blockName]: false } },
    [
      { type: PROCESS_DISMISS_BLOCK_REQUEST, payload: { blockName } },
      {
        type: PROCESS_DISMISS_BLOCK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, blockName };
        }
      },
      {
        type: PROCESS_DISMISS_BLOCK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Dashboard.Actions.processDismissBlock'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to update dashboard blocks list
 * @return {object} The RSAA object for API middleware
 */

export const updateDashboardBlock = (blocks: Object) => {
  return { type: UPDATE_DASHBOARD_BLOCK, payload: { blocks } };
};

/**
 * call to undo dismiss a block in dashboard blocks list
 * @return {object} The RSAA object for API middleware
 */

export const undoDismissBlock = (blockName: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.dashboard-blocks.change-dashboard-block',
    { body: { [blockName]: true } },
    [
      { type: UNDO_DISMISS_BLOCK_REQUEST, payload: { blockName } },
      {
        type: UNDO_DISMISS_BLOCK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data, blockName };
        }
      },
      {
        type: UNDO_DISMISS_BLOCK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Dashboard.Actions.undoDismissBlock'),
            i18n
          )
      }
    ]
  );
};
